/**
 * THE PRE-LOGIN LANDING — WHAT SCOUT IS, AND THE ONE DOOR IN.
 *
 * This is the first thing a person sees and the only surface in the product
 * that is read by somebody who is not the Owner. Everything about it follows
 * from that, and the rules are hard ones:
 *
 *   1. IT CONTAINS NO RESEARCH. No conversation, no finding, no source, no
 *      receipt, no count, no identifier, no model name. It is a static document
 *      with a mark, three sentences and a link, and it would be safe to read
 *      aloud. The brief asks for this and it is also the only defensible
 *      position: this page is served before anything has authenticated, so
 *      anything on it is public in the literal sense.
 *
 *   2. THE SIGN-IN CONTROL IS NOT DECORATION. It is an ordinary link to the
 *      workspace's own protected entry, and the ONLY way it produces a session
 *      is by causing this deployment's identity provider to act. There is no
 *      OAuth client here, no client id, no redirect this page constructs and no
 *      callback it handles — because all of that would be a second
 *      authentication implementation, and the deployment already has one.
 *
 *   3. IT DRAWS NOBODY'S LOGO. A hand-drawn provider mark beside the words
 *      "Sign in with …" would be exactly the fake the brief forbids: borrowed
 *      branding asserting a relationship this page cannot verify. So the
 *      control says what it does — "Sign in" — and the sentence beneath it says
 *      where the identity check happens. The provider is named in the release
 *      runbook, where it is a fact about a deployment rather than a picture on
 *      a page that ships to all of them. **This is also what makes the page
 *      survive a change of provider**: the Owner moved this deployment from an
 *      OAuth identity provider to Cloudflare's own one-time PIN, and the page
 *      did not change a word.
 *
 *   4. IT DOES NOT OVERCLAIM THE PRODUCT. "Autonomous" here means what it means
 *      in the constitution: Scout decides what to investigate inside a window
 *      the Owner authorizes, and has no hands. The copy says that, and the
 *      three facts below it are access facts rather than capability claims,
 *      because a capability claim on a public page is the one sentence nobody
 *      can check before signing in.
 *
 *   5. THE MOTION IS OPTIONAL AND THE PAGE IS COMPLETE WITHOUT IT. The orbit is
 *      a CSS animation on two `<g>` transforms; `prefers-reduced-motion` stops
 *      it, the shell's hidden-tab rule stops it, and the drawn positions are
 *      the resting state — so the static fallback is the page, not a degraded
 *      version of it.
 */

import { useState } from 'preact/hooks';
import { ACCESS_LOGOUT_PATH, SIGN_IN_PATH, probeSession, session, type SessionState } from '../session';

/** The one place the product's name and claim are written, so a specimen can
 *  assert the page still says what the Owner approved rather than drifting. */
export const LANDING_NAME = 'SCOUT';
export const LANDING_EYEBROW = 'AAS Scientific Intelligence';
export const LANDING_HEADLINE = 'Intelligence that investigates.';
export const LANDING_SUPPORT =
	'An autonomous scientific research intelligence built to question, investigate and learn.';

/**
 * THE MARK — an observation ring with something moving on it.
 *
 * Drawn rather than imported, for the same reason the favicon is: it is Scout's
 * own glyph and it has to be legible at 16px in a tab and at 200px on a phone,
 * which one SVG with a viewBox does and a bitmap does not.
 *
 * `aria-hidden` BECAUSE IT SAYS NOTHING. The name and the headline beside it
 * carry the meaning; a screen reader announcing "graphic" before them would be
 * noise. The whole visual is decorative and is marked as such.
 */
function OrbitMark() {
	return (
		<svg class="orbit" viewBox="0 0 200 200" aria-hidden="true" focusable="false">
			{/* THE AMBIENT GLOW IS A GRADIENT RATHER THAN A BLUR. A `filter: blur()`
			    on a 200px element is one of the most expensive things a phone can
			    be asked to paint every frame; a radial gradient is painted once
			    and costs nothing to hold. */}
			<defs>
				<radialGradient id="scout-orbit-glow" cx="50%" cy="50%" r="50%">
					<stop offset="0%" stop-color="var(--colour-accent)" stop-opacity="0.35" />
					<stop offset="55%" stop-color="var(--colour-accent)" stop-opacity="0.10" />
					<stop offset="100%" stop-color="var(--colour-accent)" stop-opacity="0" />
				</radialGradient>
			</defs>
			<circle cx="100" cy="100" r="96" fill="url(#scout-orbit-glow)" />

			{/* THE RINGS ARE STILL; ONLY THE NODES TRAVEL. Two carriers turning at
			    coprime periods, each carrying one node around a ring — so the
			    moving parts are two small circles rather than two whole ellipses,
			    and the system never settles into an obvious two-beat. */}
			<g class="orbit-ring orbit-ring--tilt">
				<ellipse cx="100" cy="100" rx="86" ry="34" />
			</g>
			<g class="orbit-ring orbit-ring--outer">
				<ellipse cx="100" cy="100" rx="86" ry="86" />
				<g class="orbit-carrier">
					<circle class="orbit-node" cx="186" cy="100" r="4" />
				</g>
			</g>
			<g class="orbit-ring orbit-ring--inner">
				<ellipse cx="100" cy="100" rx="56" ry="56" />
				<g class="orbit-carrier orbit-carrier--inner">
					<circle class="orbit-node orbit-node--quiet" cx="156" cy="100" r="2.5" />
				</g>
			</g>

			{/* THE STILL CENTRE, DRAWN IN THE FAVICON'S OWN LANGUAGE — an open ring
			    with a filled centre, which is what observation looks like as a
			    glyph. It does not move, and that is the point: the observed thing
			    is fixed and the observation moves around it. */}
			<circle class="orbit-core" cx="100" cy="100" r="11" />
			<circle class="orbit-core" cx="100" cy="100" r="3.5" fill="var(--landing-ink)" />
		</svg>
	);
}

/**
 * WHAT THE PAGE SAYS WHEN THE SESSION IS NOT SIMPLY ABSENT.
 *
 * A landing that always showed the same sign-in button would be lying in three
 * states, and all three are states this workspace can actually be in:
 *
 *   `signedOut` DURING A SESSION   the session ended while the Owner was using
 *                  the workspace. The boot case gets NO notice — a browser that
 *                  has never signed in is not being told anything it does not
 *                  already know, and a notice explaining the normal state is
 *                  how a page teaches people to ignore notices. This one is
 *                  different: something was open, and a page that silently
 *                  becomes a sign-in screen makes a person think they were
 *                  logged out at random.
 *   `notOwner`     somebody signed in, and they are not the Owner. Pressing
 *                  "Sign in" again changes nothing, so the control is replaced
 *                  by the fact.
 *   `maintenance`  Scout ANSWERED, and answered to say it is deliberately not
 *                  serving — a database behind the code, no owner credential
 *                  provisioned, a half-configured identity provider. Nothing a
 *                  visitor can do changes it, so it carries no control at all,
 *                  and it must not borrow the words of a network fault: the one
 *                  person who can fix it is told what the state actually is.
 *   `refused`      Scout ANSWERED and said no, for a reason that is not about
 *                  the session and not about the deployment being unready — the
 *                  hostname it was reached by, or a request it judged
 *                  cross-site. This is the state Phase 6A measured being
 *                  misreported as "this account is not Scout's Owner", which
 *                  told a visitor something untrue about their own identity and
 *                  closed the door while doing it. It carries no sign-in
 *                  control either: signing in is not what is wrong.
 *   `unavailable`  Scout could not be asked at all. Also not fixed by signing
 *                  in — but unlike the others it CAN be fixed by the network
 *                  coming back, so it carries the only retry on this page.
 *
 * THE RETRY IS THE ONE CONTROL HERE THAT IS NOT A LINK, and it is a real
 * operation: it re-runs the same probe the shell ran at boot. A retry that did
 * not re-ask would be the placeholder control the brief forbids.
 */
function LandingNotice({ state }: { state: SessionState }) {
	const [retrying, setRetrying] = useState(false);

	if (state.kind === 'signedOut' && state.via === 'during-session') {
		return (
			<div class="landing-notice" role="alert">
				<p class="landing-notice-head">Your session ended</p>
				<p>
					Something is still fine here: nothing you were reading has been lost. Scout refused a read
					because the session it arrived with is no longer valid, and this workspace has no
					credential of its own to renew it with.
				</p>
				<p class="landing-notice-words">
					Scout’s own words: <code>{state.error.name}</code> — {state.error.message}
				</p>
			</div>
		);
	}
	if (state.kind === 'notOwner') {
		return (
			<div class="landing-notice landing-notice--stop" role="alert">
				<p class="landing-notice-head">This account is not Scout’s Owner</p>
				<p>
					The identity was verified and it is not the one this deployment answers to. Scout has
					exactly one account, and signing in again will not change that.
				</p>
				<p class="landing-notice-words">
					Scout’s own words: <code>{state.error.name}</code> — {state.error.message}
				</p>
			</div>
		);
	}
	if (state.kind === 'maintenance') {
		// SCOUT ANSWERED. It said, in its own words, that it is deliberately not
		// serving — so this page must not describe it as a network fault, and
		// must not offer the retry that belongs to one. There is no button here
		// on purpose: nothing the reader can do changes this, and a control that
		// visibly does nothing teaches them the page's controls are decorative.
		return (
			<div class="landing-notice landing-notice--stop" role="alert">
				<p class="landing-notice-head">Scout is not serving right now</p>
				<p>
					This is a deliberate state of the deployment — not a problem with your connection, and
					not a sign-in problem. Scout answered: it replied to say that it cannot serve until an
					operator resolves this, so signing in will not help and asking again will not either.
				</p>
				<p class="landing-notice-words">
					Scout’s own words: <code>{state.error.name}</code> — {state.error.message}
				</p>
			</div>
		);
	}
	if (state.kind === 'refused') {
		// THE TWO KNOWN REASONS GET THEIR OWN SENTENCE, AND THE REST GET AN HONEST
		// GENERAL ONE. Both are facts about the DEPLOYMENT rather than about the
		// person reading — which is the whole reason this is not the `notOwner`
		// panel, whose sentence ("the identity was verified and it is not the one
		// this deployment answers to") is only true for one of them.
		const reason =
			state.error.name === 'ScoutHostNotAllowed'
				? 'This deployment answers to one hostname, and this is not it. Nothing about your account or your session is the problem — the address is.'
				: state.error.name === 'ScoutCrossSiteRefused'
					? 'The request was refused as coming from another site. Scout accepts requests from its own pages only, and this one did not look like it came from one.'
					: 'Scout answered and declined the request. This is not a session problem and not a network problem, so signing in again will not change it.';
		return (
			<div class="landing-notice landing-notice--stop" role="alert">
				<p class="landing-notice-head">Scout refused this request</p>
				<p>{reason}</p>
				<p class="landing-notice-words">
					Scout’s own words: <code>{state.error.name}</code> — {state.error.message}
				</p>
			</div>
		);
	}
	if (state.kind === 'unavailable') {
		return (
			<div class="landing-notice" role="alert">
				<p class="landing-notice-head">Scout could not be reached</p>
				<p>
					This is not a sign-in problem: the request never got a usable answer, so there is
					nothing to sign in to yet. Signing in will not help until this is resolved.
				</p>
				<p class="landing-notice-words">
					<code>{state.error.name}</code>
					{state.error.status > 0 && ` · HTTP ${state.error.status}`} — {state.error.message}
				</p>
				<button
					type="button"
					class="button ghost"
					disabled={retrying}
					onClick={() => {
						setRetrying(true);
						void probeSession().finally(() => setRetrying(false));
					}}
				>
					{retrying ? 'Asking again…' : 'Ask again'}
				</button>
			</div>
		);
	}
	return null;
}

export function Landing() {
	const state = session.value;
	// THE CONTROL IS LIVE ONLY WHEN PRESSING IT COULD DO SOMETHING. In the two
	// states above it could not, and a button that visibly does nothing is worse
	// than no button: it teaches the reader that this page's controls are
	// decorative.
	const canSignIn = state.kind === 'signedOut';

	return (
		<main class="landing" id="content">
			<div class="landing-sky" aria-hidden="true" />

			<div class="landing-inner">
				<header class="landing-brand">
					<OrbitMark />
					<p class="landing-eyebrow">{LANDING_EYEBROW}</p>
					<h1 class="landing-name">{LANDING_NAME}</h1>
					<p class="landing-headline">{LANDING_HEADLINE}</p>
					<p class="landing-support">{LANDING_SUPPORT}</p>
				</header>

				<LandingNotice state={state} />

				<section class="landing-access" aria-labelledby="landing-access-heading">
					<h2 id="landing-access-heading" class="landing-access-heading">
						Private access
					</h2>
					{canSignIn ? (
						<a class="landing-signin" href={SIGN_IN_PATH}>
							Sign in
						</a>
					) : (
						<p class="landing-signin landing-signin--inert" aria-disabled="true">
							Sign in
						</p>
					)}
					{/* WHAT A VISITOR NEEDS BEFORE PRESSING, AND THEN WHAT THEY DON'T.
					    The whole paragraph used to sit in the open, and it made the
					    product's front door read like a security briefing: four of its
					    five lines were about how authentication is implemented, in a
					    card whose job is to explain that there is one door and where it
					    goes. The sentences are all still here, word for word — the
					    first two are what a person decides on, and the rest is what
					    they check afterwards, one press away. */}
					<p class="landing-access-note">
						Scout answers to one verified Owner account. There is no sign-up and no second
						account.
					</p>
					<details class="landing-access-detail">
						<summary>What this page can and cannot do</summary>
						<p>
							The identity check happens at this deployment’s identity provider — this page
							holds no Scout credential, so it cannot sign you in and cannot sign you out.
						</p>
					</details>
				</section>

				{/* THE THREE FACTS ARE ACCESS FACTS, NOT CAPABILITY CLAIMS. Each one is
				    checkable from this repository and none of them promises what Scout
				    can do, because a promise about capability is the one kind of
				    sentence a reader cannot test before signing in. */}
				<ul class="landing-facts">
					<li>
						<span class="landing-fact-head">One account</span>
						<span class="landing-fact-body">
							The Owner identity is verified on every request, by the Worker, not by this
							page.
						</span>
					</li>
					<li>
						<span class="landing-fact-head">No credential here</span>
						<span class="landing-fact-body">
							This workspace never stores a Scout token in the browser, in storage or on the
							device.
						</span>
					</li>
					<li>
						<span class="landing-fact-head">No research on the device</span>
						<span class="landing-fact-body">
							Conversations, findings, sources and receipts are read live and are never cached
							by this page.
						</span>
					</li>
				</ul>
			</div>

			<footer class="landing-foot">
				<p>
					AAS Scout is an independent research intelligence. It is not AAS Forge, and it holds
					no AAS Forge credentials or data.
				</p>
				{/* THE SIGN-OUT DOOR EXISTED AND LED NOWHERE. This page is what a person
				    sees when the identity provider has already ended their session, so
				    offering "sign out" here would be offering to end a session that is
				    already over. It is named only so the address is not a mystery when
				    something else sends a browser to it. */}
				<p class="landing-foot-quiet">
					Session ended? The identity provider is reached at <code>{ACCESS_LOGOUT_PATH}</code>.
				</p>
			</footer>
		</main>
	);
}
